/**
 * i18n Type Definitions
 * Extracted from original translations.ts for modular structure
 */

export type Language = 'en' | 'hi' | 'ta' | 'te' | 'mr' | 'bn' | 'gu' | 'kn' | 'ml' | 'pa';

export interface TranslationStrings {
  common: {
    loading: string;
    error: string;
    retry: string;
    cancel: string;
    confirm: string;
    back: string;
    next: string;
    submit: string;
    done: string;
    close: string;
    yes: string;
    no: string;
    required: string;
    optional: string;
  };
  consent: {
    secureConsent: string;
    yourConsentRequired: string;
    consentingAs: string;
    requestingOrg: string;
    dataBeingCollected: string;
    purposeOfProcessing: string;
    whatYouWillDo: string;
    reviewDocuments: string;
    provideConsent: string;
    documentsToRead: string;
    estimatedTime: string;
    minutes: string;
    startReview: string;
    yourRights: string;
    rightToAccess: string;
    rightToCorrect: string;
    rightToWithdraw: string;
    rightToGrievance: string;
    stepOf: string;
    pleaseReadCarefully: string;
    documentsReviewed: string;
    remaining: string;
    allDocumentsReviewed: string;
    clickToRead: string;
    proceedToConsent: string;
    markAsRead: string;
    continueReading: string;
    confirmConsent: string;
    selectMethod: string;
    iAgreeToTerms: string;
    readAllDocuments: string;
    submitConsent: string;
    submitting: string;
    digitalSignature: string;
    smsVerification: string;
    emailVerification: string;
    videoRecording: string;
    audioRecording: string;
    checkboxConfirmation: string;
    drawSignature: string;
    verifyViaSms: string;
    verifyViaEmail: string;
    recordVideoConsent: string;
    recordAudioConsent: string;
    confirmByChecking: string;
    enterOtp: string;
    otpSentTo: string;
    resendOtp: string;
    verifyOtp: string;
    otpVerified: string;
    invalidOtp: string;
    startRecording: string;
    stopRecording: string;
    retake: string;
    recordingComplete: string;
    consentSubmitted: string;
    thankYou: string;
    confirmationSent: string;
    referenceNumber: string;
    errorSubmitting: string;
    pleaseReadDocs: string;
    pleaseProvideEvidence: string;
  };
  documents: {
    privacyPolicy: string;
    termsOfService: string;
    consentAgreement: string;
    dataSharingAgreement: string;
    cookiePolicy: string;
    retentionPolicy: string;
    gdprNotice: string;
    hipaaAuthorization: string;
    ccpaDisclosure: string;
    aadhaarAuthorization: string;
    customDocument: string;
  };
  dataCategories: {
    personalInfo: string;
    contactDetails: string;
    financialInfo: string;
    healthData: string;
    biometricData: string;
    locationData: string;
    behavioralData: string;
    communicationData: string;
    employmentInfo: string;
    educationInfo: string;
  };
  a11y: {
    mainContent: string;
    navigation: string;
    closeModal: string;
    expandSection: string;
    collapseSection: string;
    documentRead: string;
    documentUnread: string;
    progressIndicator: string;
    signaturePad: string;
    clearSignature: string;
    videoPlayer: string;
    audioPlayer: string;
    requiredField: string;
    errorMessage: string;
    successMessage: string;
    loadingContent: string;
    newWindow: string;
  };
  portal?: {
    // General
    myConsents: string;
    viewManageConsents: string;
    logout: string;
    welcomeBack: string;
    
    // Tenant selector
    organizationNameOrCode: string;
    orgPlaceholder: string;
    orgHint: string;
    findMyOrganization: string;
    orgNotFound: string;
    enterOrgName: string;
    or: string;
    haveDirectLink: string;
    useDirectLink: string;
    privacyProtected: string;
    
    // Login flow
    phoneOrEmail: string;
    enterPhoneOrEmailPlaceholder: string;
    otpWillBeSent: string;
    sendOtp: string;
    enterPhoneOrEmail: string;
    otpSentTo: string;
    expiresIn: string;
    enterOtp: string;
    enterValidOtp: string;
    verifyOtp: string;
    resendOtp: string;
    otpVerified: string;
    oneMoreStep: string;
    enterSecurityAnswer: string;
    enterAnswer: string;
    dobFormat: string;
    login: string;
    startOver: string;
    needHelp: string;
    privacyNotice: string;
    checkUrlOrContact: string;
    
    // Consent history
    filterAll: string;
    statusActive: string;
    statusWithdrawn: string;
    statusExpired: string;
    noConsents: string;
    noConsentsWithFilter: string;
    consentedOn: string;
    expiresOn: string;
    withdrawnOn: string;
    withdrawConsent: string;
    withdrawalHistory: string;
    help: string;
    
    // Withdrawal
    cannotWithdraw: string;
    pleaseConfirmWithdrawal: string;
    importantNotice: string;
    withdrawalNotice1: string;
    withdrawalNotice2: string;
    withdrawalNotice3: string;
    withdrawalNotice4: string;
    withdrawalReason: string;
    withdrawalReasonPlaceholder: string;
    withdrawalConfirmation: string;
    submitWithdrawalRequest: string;
    withdrawalSubmitted: string;
    withdrawalSubmittedDesc: string;
    processingTime: string;
    days: string;
    backToHistory: string;
    
    // Withdrawal history
    statusPending: string;
    statusApproved: string;
    statusRejected: string;
    noWithdrawals: string;
    viewConsents: string;
    requestedOn: string;
    processedOn: string;
    processingMessage: string;
    processingTimeInfo: string;
    
    // Help section
    yourRights: string;
    helpTitle: string;
    helpSubtitle: string;
    quickActions: string;
    viewMyConsents: string;
    viewWithdrawalRequests: string;
    loginToViewConsents: string;
    applicableLaws: string;
    
    // Rights FAQ
    rightAccessQ: string;
    rightAccessA: string;
    rightCorrectQ: string;
    rightCorrectA: string;
    rightWithdrawQ: string;
    rightWithdrawA: string;
    rightEraseQ: string;
    rightEraseA: string;
    
    // Withdrawal FAQ
    withdrawalFaq: string;
    withdrawHowQ: string;
    withdrawHowA: string;
    withdrawTimeQ: string;
    withdrawTimeA: string;
    withdrawEffectQ: string;
    withdrawEffectA: string;
    
    // Security FAQ
    securityFaq: string;
    loginSecureQ: string;
    loginSecureA: string;
    dataProtectQ: string;
    dataProtectA: string;
    
    // Contact FAQ
    contactFaq: string;
    grievanceQ: string;
    grievanceA: string;
    regulatorQ: string;
    regulatorA: string;
  };
}

export interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
];
